import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { MeetingPublic } from '@repo/types/meetings';

import { useEventVisibility } from '@repo/hooks/useEventVisibility';
import { EventGap, EventHeight, weekStartsOnMonday } from '@repo/lib/constants/calendar-constants';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { addDays, eachDayOfInterval, format, isSameDay, isSameMonth, isToday, startOfWeek } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { DraggableEvent, DroppableCell, EventItem } from '@/components/features/calendar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';
import { getCalendarEventUniqueId, type CalendarEvent, getAllEventsForDay, getEventsForDay, isHolidayCalendarEvent, getSpanningEventsForDay, sortEvents } from '@/utils/calendar';

import { useCalendarEvents } from './useCalendarEvents';

interface MonthViewProps {
  rangeDates: EventCalendarRangeDates;
  currentDate: Date;
  onEventSelect: (event: MeetingPublic) => void;
  onEventCreate: (event: Date) => void;
}

export function MonthView(monthViewProps: MonthViewProps) {
  const { rangeDates, currentDate, onEventSelect, onEventCreate } = monthViewProps;
  const { lvl } = useCredentials();
  const lvlDisabled = useMemo<boolean>(() => lvl <= 0, [lvl]);

  const days = useMemo(() => {
    return eachDayOfInterval({
      start: new Date(rangeDates.after),
      end: new Date(rangeDates.before),
    });
  }, [rangeDates]);

  const weekdays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const date = addDays(startOfWeek(new Date(), { weekStartsOn: weekStartsOnMonday }), i);
      return format(date, 'EEE');
    });
  }, []);

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    let week: Date[] = [];

    for (let i = 0; i < days.length; i++) {
      const day = days[i];
      if (day) {
        week.push(day);
      }
      if (week.length === 7 || i === days.length - 1) {
        result.push(week);
        week = [];
      }
    }

    return result;
  }, [days]);

  const { events, isError, error, refetch } = useCalendarEvents(rangeDates);

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isHolidayCalendarEvent(event)) return;
    onEventSelect(event);
  };

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const { contentRef, getVisibleEventCount } = useEventVisibility({
    eventHeight: EventHeight,
    eventGap: EventGap,
  });

  useEffect(() => {
    if (isError) {
      toasterMessageHandler(baseErrorMessages, error, {
        action: (
          <Button
            size={'sm'}
            className={'ml-auto'}
            variant={'tertiary'}
            onClick={() => {
              void refetch();
            }}
          >
            Ponów
          </Button>
        ),
      });
    }
  }, [error, isError, refetch]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <div className="md:bg-background bg-background-dark divide-border-primary/70 sticky top-(--calendar-header-height) z-20 grid grid-cols-7 divide-x border-y">
        {weekdays.map((day) => (
          <span key={day} className="text-muted-foreground py-2 text-center text-sm">
            {day}
          </span>
        ))}
      </div>
      <div className="grid flex-1 auto-rows-fr">
        {weeks.map((week, weekIndex) => (
          <div key={`week-${weekIndex}`} className="grid grid-cols-7 [&:last-child>*]:border-b-0">
            {week.map((day, dayIndex) => {
              const dayEvents = getEventsForDay(events, day);
              const spanningEvents = getSpanningEventsForDay(events, day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const cellId = `month-cell-${day.toISOString()}`;
              const allDayEvents = [...spanningEvents, ...dayEvents];
              const allEvents = getAllEventsForDay(events, day);

              const isReferenceCell = weekIndex === 0 && dayIndex === 0;
              const visibleCount = isMounted ? getVisibleEventCount(allDayEvents.length) : undefined;
              const hasMore = visibleCount !== undefined && allDayEvents.length > visibleCount;
              const remainingCount = hasMore ? allDayEvents.length - visibleCount : 0;
              return (
                <div
                  key={day.toString()}
                  className="group border-border-primary/70 data-outside-cell:bg-accent/45 data-outside-cell:text-muted-foreground border-r border-b last:border-r-0"
                  data-today={isToday(day) || undefined}
                  data-outside-cell={!isCurrentMonth || undefined}
                >
                  <DroppableCell
                    id={cellId}
                    date={day}
                    disabled={lvlDisabled}
                    onClick={() => {
                      const startTime = new Date(day);
                      startTime.setHours(9);
                      startTime.setMinutes(0);
                      startTime.setSeconds(0);
                      startTime.setMilliseconds(0);
                      onEventCreate(startTime);
                    }}
                  >
                    <span className="group-data-today:bg-brand-accent group-data-today:text-brand inline-flex size-6 items-center justify-center rounded-xs text-xs">{format(day, 'd')}</span>
                    <div
                      ref={isReferenceCell ? contentRef : null}
                      className="min-h-[calc((var(--event-height)+var(--event-gap))*2)] sm:min-h-[calc((var(--event-height)+var(--event-gap))*3)] lg:min-h-[calc((var(--event-height)+var(--event-gap))*4)]"
                    >
                      {sortEvents(allDayEvents).map((event, index) => {
                        const eventStart = new Date(event.startDate);
                        const eventEnd = new Date(event.endDate);
                        const isFirstDay = isSameDay(day, eventStart);
                        const isLastDay = isSameDay(day, eventEnd);

                        const isHidden = isMounted && visibleCount && index >= visibleCount;

                        if (!visibleCount) return null;

                        if (!isFirstDay) {
                          return (
                            <div key={`spanning-${getCalendarEventUniqueId(event)}-${day.toISOString().slice(0, 10)}`} className="aria-hidden:hidden" aria-hidden={isHidden ? 'true' : undefined}>
                              <EventItem onClick={(e) => handleEventClick(event, e)} event={event} view={VIEW_KEY.MONTH} isFirstDay={isFirstDay} isLastDay={isLastDay}>
                                <div className="invisible" aria-hidden={true}>
                                  {!event.allDay && <span>{format(new Date(event.startDate), 'h:mm')} </span>}
                                  {event.name}
                                </div>
                              </EventItem>
                            </div>
                          );
                        }

                        return (
                          <div key={getCalendarEventUniqueId(event)} className="aria-hidden:hidden" aria-hidden={isHidden ? 'true' : undefined}>
                            <DraggableEvent
                              event={event}
                              view={VIEW_KEY.MONTH}
                              disabled={lvlDisabled || isHolidayCalendarEvent(event)}
                              onClick={(e) => handleEventClick(event, e)}
                              isFirstDay={isFirstDay}
                              isLastDay={isLastDay}
                            />
                          </div>
                        );
                      })}

                      {hasMore && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="outline-focus-ring text-muted-foreground hover:text-foreground hover:bg-accent/75 mt-(--event-gap) flex h-(--event-height) w-full items-center overflow-hidden px-1 text-left text-[10px] outline-0 backdrop-blur-md transition outline-none select-none focus-visible:outline-3 data-dragging:cursor-grabbing data-dragging:shadow-lg data-past-event:line-through sm:px-2 sm:text-xs"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>
                                + {remainingCount} <span className="max-sm:sr-only">więcej</span>
                              </span>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="center"
                            className="max-w-52 p-3"
                            style={
                              {
                                '--event-height': `${EventHeight}px`,
                              } as React.CSSProperties
                            }
                          >
                            <div className="space-y-2">
                              <div className="text-sm font-medium">{format(day, 'EEE d')}</div>
                              <div className="space-y-1">
                                {sortEvents(allEvents).map((event) => {
                                  const eventStart = new Date(event.startDate);
                                  const eventEnd = new Date(event.endDate);
                                  const isFirstDay = isSameDay(day, eventStart);
                                  const isLastDay = isSameDay(day, eventEnd);

                                  return (
                                    <EventItem
                                      key={getCalendarEventUniqueId(event)}
                                      onClick={(e) => handleEventClick(event, e)}
                                      event={event}
                                      view={VIEW_KEY.MONTH}
                                      isFirstDay={isFirstDay}
                                      isLastDay={isLastDay}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </DroppableCell>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
